import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ProfileListItem,
  ProfileRolePayload,
  ProfileShow,
  ProfileWrite,
  ProfilesListQuery,
  ProfilesListResponse,
} from '@/api/generated/types'
import type { PlayerPosition, StaffPosition } from '@/lib/profile-roles'

export type { ProfileListItem, ProfileShow, ProfileWrite }

/** List query for GET /profiles — uses documented `page_size`. */
export type ProfileListParams = Pick<
  ProfilesListQuery,
  'page' | 'page_size' | 'query' | 'server' | 'without_accounts'
>

/**
 * PUT body built for the OpenAPI `ProfileWrite` schema (snake_case).
 * `league_player` / `staff` are empty `object` in the spec; we send `{ position }` which the API accepts.
 */
export type ProfileUpdateInput = {
  name: string
  country: string
  other_countries: string[]
  regions?: { uuid: string }[]
  league_player: { position: PlayerPosition } | null
  staff: { position: StaffPosition } | null
}

export type ProfileDetail = ProfileShow

export const listProfiles = (params: ProfileListParams, signal?: AbortSignal) =>
  api.get<ProfilesListResponse>('/profiles', { params, signal }).then((r) => r.data)

export const getProfile = (uuid: string, signal?: AbortSignal) =>
  api.get<ProfileShow>(`/profiles/${uuid}`, { signal }).then((r) => r.data)

export const createProfile = (body: ProfileUpdateInput) =>
  api.post<ProfileShow>('/profiles', body as ProfileWrite).then((r) => r.data)

export const updateProfile = (uuid: string, body: ProfileUpdateInput) =>
  api.put<ProfileShow>(`/profiles/${uuid}`, body as ProfileWrite).then((r) => r.data)

export const deleteProfile = (uuid: string) => api.delete(`/profiles/${uuid}`).then(() => undefined)

export const refreshProfile = (uuid: string) =>
  api.get<ProfileShow>(`/profiles/${uuid}/refresh`).then((r) => r.data)

export function asProfileRole(value: ProfileListItem['league_player'] | ProfileListItem['staff']) {
  return value as ProfileRolePayload | null | undefined
}

/**
 * A player object is present but `accounts` was omitted, so linkage cannot be decided.
 * `league_player: null` means there is no player and therefore no Riot accounts.
 */
export function profileAccountsUnknown(profile: Pick<ProfileListItem, 'league_player'>): boolean {
  const role = asProfileRole(profile.league_player)
  return role != null && !Array.isArray(role.accounts)
}

export function profileHasAccounts(profile: Pick<ProfileListItem, 'league_player'>): boolean {
  const accounts = asProfileRole(profile.league_player)?.accounts
  if (!Array.isArray(accounts)) return false
  return accounts.length > 0
}

export function profileMayDelete(profile: Pick<ProfileListItem, 'league_player'>): boolean {
  if (profileAccountsUnknown(profile)) return false
  return !profileHasAccounts(profile)
}

/** Timestamp fields are returned by the API but not yet on ProfileListItem in the OpenAPI schema. */
export function profileUpdatedAt(profile: ProfileListItem): string | undefined {
  const value = (profile as ProfileListItem & { updated_at?: unknown }).updated_at
  return typeof value === 'string' ? value : undefined
}

export const profileQueries = {
  all: ['profiles'] as const,
  list: (params: ProfileListParams) =>
    queryOptions({
      queryKey: ['profiles', 'list', params],
      queryFn: ({ signal }) => listProfiles(params, signal),
      placeholderData: (previous) => previous,
    }),
  detail: (uuid: string) =>
    queryOptions({
      queryKey: ['profiles', 'detail', uuid],
      queryFn: ({ signal }) => getProfile(uuid, signal),
    }),
}
